import styles from './RegisterCard.module.css';
import { AceCard } from '../AceCard';
import { LendersForTier } from './components/LendersForTier';
import { LenderSelectActionButtons } from './components/LenderSelectActionButtons';
import { LoanRequestActionButtons } from './components/LoanRequestActionButtons';
import { SelectApplicantMode } from './components/SelectApplicantMode';
import {
  lender_filter_hooks,
  useLenders,
  useLoanApplication,
} from '@axo/shared/data-access/hooks';
import { useCreateLoanRequestFromFormValues } from '../ApplicationData/ApplicationDataForm/createLoanRequestFromFormValues';
import { useFormContext } from 'react-hook-form';
import { ApplicationDataFormValues } from '../ApplicationData/ApplicationDataForm/ApplicationDataFormSchema';
import { useMemo } from 'react';
import { Product } from '@axo/shared/data-access/types';
import { AvailableLender } from './IAvailableLenders';
import { MarketCountryCode } from '@axo/shared/types';
import { ApplicationDataCommentTabPanel } from '../ApplicationData/ApplicationDataApplicantsTabs/ApplicationDataCommentTabPanel';

export interface RegisterCardProps {
  applicationID: string;
}

export function RegisterCard({ applicationID }: RegisterCardProps) {
  const { data: applicationData } = useLoanApplication(applicationID);
  const { data: lendersData } = useLenders(applicationData?.MarketCountry);
  const createLoanRequestFromFormValues =
    useCreateLoanRequestFromFormValues(applicationID);
  const { getValues } = useFormContext<ApplicationDataFormValues>();
  const isSecuredLoan = applicationData?.Product === Product.SecuredLoan;

  const supportedLenders = useMemo(
    () =>
      lendersData?.filter(({ BrokerConfiguration }) =>
        BrokerConfiguration?.some((config) => {
          if (config.Broker !== applicationData?.Meta?.Broker) return false;

          switch (applicationData.Product) {
            case Product.UnsecuredLoan:
              return Boolean(config.OffersUnsecuredLoan);
            case Product.SecuredLoan:
              return Boolean(config.OffersSecuredLoan);
            case Product.CreditCard:
              return Boolean(config.OffersCreditCard);
            default:
              return false;
          }
        }),
      ),
    [applicationData?.Meta?.Broker, applicationData?.Product, lendersData],
  );

  const loanRequestTemplate = createLoanRequestFromFormValues(
    null,
    getValues(),
  );

  const { data: lendersFilterData, isFetching: lendersFilterDataIsFetching } =
    lender_filter_hooks.useLenderFilterCheck({
      loanRequest: loanRequestTemplate,
      lenderIDs:
        supportedLenders
          ?.filter((lender) => {
            const config = lender.BrokerConfiguration?.find(
              (config) => config.Broker === applicationData?.Meta?.Broker,
            );

            return !config?.Disabled;
          })
          .map((lender) => lender.ID) || [],
    });

  const availableLenders: AvailableLender[] = useMemo(
    () =>
      supportedLenders?.map((lender) => {
        const config = lender.BrokerConfiguration?.find(
          (config) => config.Broker === applicationData?.Meta?.Broker,
        );
        const disabled = Boolean(config?.Disabled);

        const lenderFilter = lendersFilterData?.[lender.ID];

        let state: AvailableLender['state'] = {
          disabled: false,
          loading: lendersFilterDataIsFetching,
        };

        if (disabled) {
          state = {
            disabled: true,
            reason: 'Lender is disabled',
            loading: false,
          };
        } else if (lenderFilter?.length) {
          state = {
            disabled: true,
            reason: (
              <div>
                <span>
                  <b>Lender Filter Results</b>
                </span>
                <br />
                <ul>
                  {lenderFilter.map((filter) => (
                    <li key={filter.ID}>{filter.Result}</li>
                  ))}
                </ul>
              </div>
            ),
            loading: false,
          };
        }

        return {
          lenderID: lender.ID,
          state,
          tier: isSecuredLoan ? 0 : (config?.Tier ?? 1),
          shortName: lender.ShortName,
          name: lender.Name,
        };
      }) ?? [],
    [
      applicationData?.Meta?.Broker,
      lendersFilterData,
      lendersFilterDataIsFetching,
      supportedLenders,
      isSecuredLoan,
    ],
  );

  const lendersByTier = useMemo(() => {
    const map: Record<number, AvailableLender[]> = {};

    availableLenders.forEach((lender) => {
      if (!map[lender.tier]) map[lender.tier] = [];

      map[lender.tier].push(lender);
    });

    return map;
  }, [availableLenders]);

  if (!applicationData) return null;

  return (
    <AceCard
      noContentPadding
      isExpandedByDefault
      title="Register loan request"
      id={`RegisterCard.${applicationID}`}
    >
      <div className={styles.registrationContainer}>
        <div className={styles.actionBar}>
          <LoanRequestActionButtons
            applicationID={applicationID}
            isLoading={lendersFilterDataIsFetching}
          />
        </div>
        <div className={styles.applicationModeHeader}>Send application as:</div>
        <div className={styles.applicationModeSelector}>
          <SelectApplicantMode applicationID={applicationID} />
        </div>
        <div className={styles.lenderSelectHeader}>Send application to:</div>
        <div className={styles.lenderSelectorContainer}>
          <div className={styles.lenderSelector}>
            {lendersByTier &&
              Object.entries(lendersByTier).map(([tier, lenders]) => (
                <LendersForTier
                  applicationID={applicationID}
                  key={tier}
                  tier={tier}
                  lenders={lenders}
                  marketCountry={
                    applicationData?.MarketCountry as MarketCountryCode
                  }
                />
              ))}
          </div>
          {applicationData.Product === Product.SecuredLoan ? (
            <div className={styles.applicationCommentSection}>
              <ApplicationDataCommentTabPanel
                applicationID={applicationID}
                viewOnly={true}
              />
            </div>
          ) : null}
        </div>
        <div className={styles.lenderSelectButtons}>
          <LenderSelectActionButtons
            lenders={availableLenders}
            applicationID={applicationID}
          />
        </div>
      </div>
    </AceCard>
  );
}
